import { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { InstallationSchedule } from "../../types";
import { coloreStato, STATO_ORDER } from "../../utils/statoColors";
import { useCountUp } from "../../hooks/useCountUp";
import { Badge } from "./Badge";
import { Prossimi7Giorni } from "./Prossimi7Giorni";

/** Card di uno stato del calendario — isolata in un componente proprio così
 * ognuna anima il proprio numero in modo indipendente (le regole di React
 * non permettono di chiamare useCountUp dentro un .map di un altro componente).
 * `to` assente = tessera non cliccabile: serve al ruolo cliente, che non ha
 * accesso alla pagina Installazioni (/schedule). */
function StatoCard({ stato, count, isLoading, to }: { stato: string; count: number; isLoading: boolean; to?: string }) {
  const animated = useCountUp(count);
  const inner = (
    <>
      <Badge stato={stato}>{stato}</Badge>
      <p className="mt-3 text-2xl font-bold tabular-nums text-ink">{isLoading ? "—" : animated}</p>
    </>
  );
  // `min-w-0`: senza, un grid item non può restringersi sotto la larghezza
  // intrinseca del contenuto e il badge sborderebbe invece di troncarsi.
  // `title`: se il badge viene troncato, il nome completo resta leggibile al hover.
  // `--stato`: al passaggio del mouse il bordo prende il colore dello stato
  // (rosso su "Da programmare", verde su "Effettuata"…), mentre a riposo resta
  // grigio neutro. Serve una CSS variable perché il colore è dinamico e Tailwind
  // non può generare a build time una classe per ogni stato.
  const colore = coloreStato(stato) ?? "#6b7280";
  const styleVar = { "--stato": colore } as CSSProperties;
  const base =
    "min-w-0 rounded-card border border-line bg-white p-4 shadow-card transition-colors duration-150 hover:border-[color:var(--stato)]";
  if (!to)
    return (
      <div className={base} style={styleVar} title={stato}>
        {inner}
      </div>
    );
  return (
    <Link
      to={to}
      title={stato}
      style={styleVar}
      className={`${base} block transition-[box-shadow,transform,border-color] duration-150 hover:-translate-y-0.5 hover:shadow-lift`}
    >
      {inner}
    </Link>
  );
}

/** Donut compatto della stessa distribuzione per stato già mostrata dalle
 * StatoCard — stesso dato, vista d'insieme delle proporzioni invece dei
 * singoli conteggi. Nascosto finché non ci sono installazioni da mostrare,
 * per non disegnare una ciambella vuota. */
function DistribuzionePerStatoChart({ byStato, isLoading }: { byStato: Record<string, number>; isLoading: boolean }) {
  const data = STATO_ORDER.map((stato) => ({ stato, value: byStato[stato] ?? 0 })).filter((d) => d.value > 0);
  const totale = data.reduce((sum, d) => sum + d.value, 0);

  if (isLoading || totale === 0) return null;

  return (
    <div className="rounded-card border border-line bg-white p-4 shadow-card">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/50">Distribuzione</p>
      <div className="flex items-center gap-3">
        <div className="h-28 w-28 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="stato" innerRadius={34} outerRadius={54} paddingAngle={3} cornerRadius={3}>
                {data.map((d) => (
                  <Cell key={d.stato} fill={coloreStato(d.stato) ?? "#6b7280"} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="flex min-w-0 flex-col gap-1.5">
          {data.map((d) => (
            <li key={d.stato} className="flex items-center gap-1.5 text-xs text-ink/70">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: coloreStato(d.stato) ?? "#6b7280" }} />
              <span className="truncate">{d.stato}</span>
              <span className="ml-auto shrink-0 font-semibold tabular-nums text-ink">{d.value}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

interface InstallazioniPerStatoProps {
  byStato: Record<string, number>;
  prossimi7Giorni: InstallationSchedule[];
  isLoading: boolean;
  /**
   * Se true, tessere e righe rimandano alla pagina Installazioni (/schedule).
   * Va disattivato per il ruolo cliente, che non ha accesso a quella pagina —
   * per lui i contatori sono di sola consultazione dentro il Calendario.
   */
  linkToInstallazioni?: boolean;
}

/**
 * Card "Installazioni — per stato": conteggi per stato + donut della
 * distribuzione + elenco dei prossimi 7 giorni. UNICA implementazione, usata
 * sia dalla Dashboard (ruoli interni) sia dal Calendario unificato (ruolo
 * cliente, che non ha la Dashboard). I dati arrivano sempre da
 * GET /api/schedule/stats, che è già scopizzato lato server con
 * buildScopedWhere: admin/staff vedono tutto, CA/OT il proprio centro, il
 * cliente solo le installazioni delle sue aziende.
 */
export function InstallazioniPerStato({
  byStato,
  prossimi7Giorni,
  isLoading,
  linkToInstallazioni = true,
}: InstallazioniPerStatoProps) {
  return (
    <>
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Installazioni — per stato</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_260px]">
        {/* 5 colonne solo da 2xl (1536px). Sotto, il contenuto è cappato da
            max-w-7xl e il donut si prende 260px fissi: cinque card starebbero
            in ~93-125px e i badge più lunghi ("Da programmare") verrebbero
            troncati. Con tre colonne restano larghe ~200px e il testo è sempre
            integro — il truncate del Badge resta come rete di sicurezza. */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 2xl:grid-cols-5">
          {STATO_ORDER.map((stato) => (
            <StatoCard
              key={stato}
              stato={stato}
              count={byStato[stato] ?? 0}
              isLoading={isLoading}
              to={linkToInstallazioni ? `/schedule?stato=${encodeURIComponent(stato)}` : undefined}
            />
          ))}
        </div>
        <DistribuzionePerStatoChart byStato={byStato} isLoading={isLoading} />
      </div>

      <div className="mt-8">
        <Prossimi7Giorni
          records={prossimi7Giorni}
          isLoading={!!isLoading}
          linkToInstallazioni={linkToInstallazioni}
        />
      </div>
    </>
  );
}
